import { createContext, useContext, type ReactNode } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { collection, query, orderBy } from 'firebase/firestore';
import type { QuerySnapshot, DocumentData, FirestoreError } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';

export type CollectionState = [
  QuerySnapshot<DocumentData> | undefined,
  boolean,
  FirestoreError | undefined,
];

interface AppDataContextValue {
  clientsState: CollectionState;
  projectsState: CollectionState;
  suppliersState: CollectionState;
  ordersState: CollectionState;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const clientsState = useCollection(
    query(collection(db, ...splitPath(COLLECTIONS.elka.clients)), orderBy('createdAt', 'desc'))
  ) as unknown as CollectionState;

  const projectsState = useCollection(
    query(collection(db, ...splitPath(COLLECTIONS.elka.projects)), orderBy('createdAt', 'desc'))
  ) as unknown as CollectionState;

  const suppliersState = useCollection(
    query(collection(db, ...splitPath(COLLECTIONS.elka.suppliers)), orderBy('createdAt', 'desc'))
  ) as unknown as CollectionState;

  const ordersState = useCollection(
    query(collection(db, ...splitPath(COLLECTIONS.elka.orders)), orderBy('orderDate', 'desc'))
  ) as unknown as CollectionState;

  return (
    <AppDataContext.Provider value={{ clientsState, projectsState, suppliersState, ordersState }}>
      {children}
    </AppDataContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}
